import Konva from 'konva';
import { ArrowRight, Circle as CircleIcon, Hand, Minus, Move, Pencil, Pipette, Plus, Redo2, Square, Trash2, Type, Undo2 } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Circle, Image, Text as KonvaText, Layer, Line, Rect, Stage, Transformer } from 'react-konva';
import { Html } from "react-konva-utils";
import { useDispatch, useSelector } from 'react-redux';
import { Button, ColorPicker, ResponsiveDialog } from 'ui';
import { cn } from 'utils/cn';
import { getSelectedSiteInfo } from '../../../../store/siteInfoSlice';
import { getAnnotateImage, getAnnotateModalOpen, setAnnotateModalOpen } from '../../../../store/tasksSlice';


interface Shape {
    id: string;
    isEditing?: boolean;
    type: 'rect' | 'circle' | 'text' | 'arrow';
    x: number;
    y: number;
    width?: number;
    height?: number;
    radius?: number;
    text?: string;
    points?: number[];
    fill?: string;
    stroke?: string;
    isTransforming?: boolean;
    strokeWidth?: number;
    fontSize?: number;
    scaleX?: number;
    scaleY?: number;
}

interface DrawLine {
    id: string;
    points: number[];
    strokeWidth?: number;
    stroke: string;
}

interface ToolButton {
    icon: JSX.Element;
    name: 'move' | 'rect' | 'circle' | 'pen' | 'pan' | 'text' | 'arrow';
    tooltip: string;
}

interface PhotoEditorProps {
    setFiles: React.Dispatch<React.SetStateAction<File[]>>;
    files: File[];
}

const PhotoEditor = ({ setFiles, files }: PhotoEditorProps) => {
    const dispatch = useDispatch();
    const siteInfo = useSelector(getSelectedSiteInfo);
    const [tool, setTool] = useState<ToolButton['name']>('rect');
    const [tempTool, setTempTool] = useState<ToolButton['name'] | null>(null);
    const [shapes, setShapes] = useState<Shape[]>([]);
    const [lines, setLines] = useState<DrawLine[]>([]);
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [history, setHistory] = useState<{ shapes: Shape[], lines: DrawLine[] }[]>([]);
    const [historyStep, setHistoryStep] = useState(0);
    const [scale, setScale] = useState(1);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [isDrawing, setIsDrawing] = useState(false);
    const [color, setColor] = useState('#ff0000');
    const [stageSize, setStageSize] = useState({ width: 800, height: 600 });
    const stageRef = useRef<Konva.Stage>(null);
    const trRef = useRef<Konva.Transformer>(null);
    const [size, setSize] = useState(4);
    const containerRef = useRef<HTMLDivElement>(null);
    const [editingId, setEditingId] = useState<string | null>(null);
    const annotateModalOpen = useSelector(getAnnotateModalOpen);
    const annotateImage = useSelector(getAnnotateImage);
    const [imageWidth, setImageWidth] = useState(0);
    const [imageHeight, setImageHeight] = useState(0);

    // Modify the image's onload function
    const [image] = useState(() => {
        const img = new window.Image();
        img.onload = () => {
            // Store image dimensions for later use
            setImageWidth(img.width);
            setImageHeight(img.height);

            // Wait a tick to ensure container is properly rendered
            setTimeout(() => {
                if (containerRef.current) {
                    const padding = 16;
                    const containerWidth = containerRef.current.clientWidth - padding;
                    const containerHeight = containerRef.current.clientHeight - padding;

                    // Calculate scale ratios
                    const scaleX = containerWidth / img.width;
                    const scaleY = containerHeight / img.height;

                    // Use the smaller scale to ensure image fits both dimensions
                    const newScale = Math.min(scaleX, scaleY);
                    setScale(newScale);

                    // Calculate center position
                    const centerX = (containerWidth - (img.width * newScale)) / 2;
                    const centerY = (containerHeight - (img.height * newScale)) / 2;

                    // Set the position to center the image
                    setPosition({ x: centerX, y: centerY });

                    // Update stage size to match container
                    setStageSize({
                        width: containerWidth,
                        height: containerHeight
                    });
                }
            }, 0);
        };
        return img;
    });

    useEffect(() => {
        image.src = annotateImage;
        // Reset position and scale when loading a new image
        // We'll let the onload handler set the proper values
    }, [image, annotateImage]);

    // Fit image to screen function
    const fitToScreen = useCallback(() => {
        if (containerRef.current && imageWidth && imageHeight) {
            const padding = 16;
            const containerWidth = containerRef.current.clientWidth - padding;
            const containerHeight = containerRef.current.clientHeight - padding;

            const scaleX = containerWidth / imageWidth;
            const scaleY = containerHeight / imageHeight;
            const newScale = Math.min(scaleX, scaleY);

            setScale(newScale);

            const centerX = (containerWidth - (imageWidth * newScale)) / 2;
            const centerY = (containerHeight - (imageHeight * newScale)) / 2;

            setPosition({ x: centerX, y: centerY });
        }
    }, [imageWidth, imageHeight]);

    useEffect(() => {
        if (!annotateModalOpen) {
            // clear the image and stage
            image.src = '';
            stageRef.current?.destroyChildren();
            // Reset shapes and lines
            setShapes([]);
            setLines([]);
            // Reset history
            setHistory([]);
            setHistoryStep(0);
            // Reset selected shape
            setSelectedId(null);
            // Reset editing state
            setEditingId(null);
            // Reset image dimensions
            setImageWidth(0);
            setImageHeight(0);
        }
    }, [annotateModalOpen, image]);

    const getRelativePointerPosition = (stage: Konva.Stage | null) => {
        if (!stage) return { x: 0, y: 0 };
        const transform = stage.getAbsoluteTransform().copy();
        transform.invert();
        const pos = stage.getPointerPosition();
        if (!pos) return { x: 0, y: 0 };
        return transform.point(pos);
    };


    const updateAnnotateModalOpen = (open: boolean) => {
        dispatch(setAnnotateModalOpen(open));
    };


    const updateSelectedShapeSize = (newSize: number) => {
        if (!selectedId) return;

        const shapes_copy = [...shapes];
        const shape = shapes_copy.find(s => s.id === selectedId);

        if (!shape) return;

        if (shape.type === 'text') {
            shape.fontSize = newSize * 8; // Scale up for text
        } else {
            shape.strokeWidth = newSize;
        }

        setShapes(shapes_copy);
        addToHistory();
    };

    const getCursor = () => {
        switch (tool) {
            case 'move':
                return 'move';
            case 'pan':
                return isDragging ? 'grabbing' : 'grab';
            case 'rect':
            case 'circle':
            case 'arrow':
                return 'crosshair';
            case 'text':
                return 'text';
            case 'pen':
                return 'crosshair';
            default:
                return 'default';
        }
    };

    const addToHistory = useCallback(() => {
        const newHistory = history.slice(0, historyStep + 1);
        newHistory.push({ shapes: [...shapes], lines: [...lines] });
        setHistory(newHistory);
        setHistoryStep(newHistory.length - 1);
    }, [history, historyStep, shapes, lines]);

    const undo = useCallback(() => {
        if (historyStep > 0) {
            const prevStep = historyStep - 1;
            setShapes(history[prevStep].shapes);
            setLines(history[prevStep].lines);
            setHistoryStep(prevStep);
        }
    }, [history, historyStep]);

    const redo = useCallback(() => {
        if (historyStep < history.length - 1) {
            const nextStep = historyStep + 1;
            setShapes(history[nextStep].shapes);
            setLines(history[nextStep].lines);
            setHistoryStep(nextStep);
        }
    }, [history, historyStep]);

    const handleMouseDown = (e: Konva.KonvaEventObject<MouseEvent>) => {
        const stage = e.target.getStage();
        const clickedOnEmpty = e.target === stage;
        const clickedOnShape = !clickedOnEmpty;

        // If clicking on a shape, prevent stage dragging
        if (clickedOnShape) {
            stage?.draggable(false);
        }

        if (tool === 'move') {
            // If clicking on empty space, deselect
            if (clickedOnEmpty) {
                setSelectedId(null);
                return;
            }

            // If clicking on a shape, select it
            const clickedOnShape = e.target;
            if (clickedOnShape) {
                const shapeId = clickedOnShape.id();
                // Only set selectedId if we actually clicked a shape (has an id)
                if (shapeId) {
                    setSelectedId(shapeId);
                    return;
                }
            }
            return;
        }

        if (tool === 'pan') {
            setIsDragging(true);
            return;
        }

        setIsDrawing(true);
        const pos = getRelativePointerPosition(stage);
        const id = Math.random().toString();

        if (tool === 'text') {
            const defaultText = 'Double click to edit';
            const textWidth = defaultText.length * (size * 8 * 0.6); // Approximate width based on font size
            const newShape: Shape = {
                id,
                type: 'text',
                x: pos.x,
                y: pos.y,
                text: defaultText,
                fill: color,
                fontSize: size * 8,
                width: Math.max(150, textWidth), // Use the larger of our minimum width or calculated width
                height: size * 8 * 1.5 // Height based on font size with some padding
            };
            setShapes([...shapes, newShape]);
            setSelectedId(id);
            setEditingId(id);
            return;
        }

        if (tool === 'arrow') {
            const newShape: Shape = {
                id,
                type: 'arrow',
                x: pos.x,
                y: pos.y,
                points: [0, 0, 0, 0],
                stroke: color,
                strokeWidth: size
            };
            setShapes([...shapes, newShape]);
            setSelectedId(id);
            return;
        }

        if (tool === 'pen') {
            setIsDrawing(true);
            setLines([...lines, {
                id,
                points: [pos.x, pos.y],
                stroke: color,
                strokeWidth: size
            }]);
            return;
        }

        const newShape: Shape = tool === 'rect'
            ? {
                id,
                type: 'rect',
                x: pos.x,
                y: pos.y,
                width: 0,
                height: 0,
                stroke: color,
                strokeWidth: size
            }
            : {
                id,
                type: 'circle',
                x: pos.x,
                y: pos.y,
                radius: 0,
                stroke: color,
                strokeWidth: size
            };
        setShapes([...shapes, newShape]);
        setSelectedId(id);
    };


    const updateSelectedShapeColor = (newColor: string) => {
        if (!selectedId) return;

        const shapes_copy = [...shapes];
        const shape = shapes_copy.find(s => s.id === selectedId);

        if (!shape) return;

        if (shape.type === 'text') {
            shape.fill = newColor;
        } else {
            shape.stroke = newColor;
        }

        setShapes(shapes_copy);
        addToHistory();
    };

    const handleMouseMove = (e: Konva.KonvaEventObject<MouseEvent>) => {
        const stage = e.target.getStage();

        if (tool === 'pan' && isDragging) {
            return;
        }

        const pos = getRelativePointerPosition(stage);

        // Only handle pen drawing if we're actually drawing
        if (tool === 'pen' && isDrawing) {
            const lastLine = lines[lines.length - 1];
            lastLine.points = lastLine.points.concat([pos.x, pos.y]);
            setLines([...lines.slice(0, -1), lastLine]);
            return;
        }

        // Only handle shape updates if we have a selected shape AND we're actively drawing
        if (!selectedId || !isDrawing) return;

        const shapes_copy = [...shapes];
        const shape = shapes_copy.find(s => s.id === selectedId);
        if (!shape) return;

        if (shape.type === 'arrow') {
            shape.points = [0, 0, pos.x - shape.x, pos.y - shape.y];
        } else if (shape.type === 'rect') {
            shape.width = pos.x - shape.x;
            shape.height = pos.y - shape.y;
        } else if (shape.type === 'circle') {
            const dx = pos.x - shape.x;
            const dy = pos.y - shape.y;
            shape.radius = Math.sqrt(dx * dx + dy * dy);
        }

        setShapes(shapes_copy);
    };

    const handleMouseUp = () => {
        if (stageRef.current) {
            stageRef.current.draggable(tool === 'pan');
        }

        if (isDrawing || selectedId) {
            addToHistory();
        }

        // Only switch to move tool if we just finished creating a shape
        // (excluding pan and move tools since they don't create shapes)
        if (isDrawing && tool !== 'pan' && tool !== 'move') {
            // For pen tool, select the last line
            if (tool === 'pen') {
                const lastLine = lines[lines.length - 1];
                if (lastLine) {
                    setSelectedId(lastLine.id);
                }
            }
            // For all other tools, the selectedId is already set during creation
            setTool('move');
        }

        setIsDrawing(false);
        setIsDragging(false);
    };

    const deleteSelected = useCallback(() => {
        if (!selectedId) return;

        const newShapes = shapes.filter(shape => shape.id !== selectedId);
        const newLines = lines.filter(line => line.id !== selectedId);

        setShapes(newShapes);
        setLines(newLines);
        setSelectedId(null);
        addToHistory();
    }, [selectedId, shapes, lines, addToHistory]);

    useEffect(() => {
        addToHistory();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    useEffect(() => {
        const updateSize = () => {
            if (containerRef.current) {
                const padding = 8;
                setStageSize({
                    width: containerRef.current.clientWidth - padding * 2,
                    height: containerRef.current.clientHeight - padding * 2,
                });
            }
        };

        updateSize();
        window.addEventListener('resize', updateSize);
        return () => window.removeEventListener('resize', updateSize);
    }, []);

    useEffect(() => {
        // Only add event listeners if the photo editor modal is open
        if (annotateModalOpen) {
            const handleKeyDown = (e: KeyboardEvent) => {
                // If we're editing text, don't handle any keyboard shortcuts
                if (editingId) {
                    return;
                }

                // Space for pan tool
                if (e.code === 'Space' && !tempTool) {
                    e.preventDefault();
                    setTempTool(tool);
                    setTool('pan');
                }

                // Undo/Redo
                if (e.metaKey || e.ctrlKey) {
                    if (e.key === 'z' && !e.shiftKey) {
                        e.preventDefault();
                        undo();
                    }
                    if (e.key === 'z' && e.shiftKey) {
                        e.preventDefault();
                        redo();
                    }
                }

                // Delete/Backspace
                if ((e.key === 'Delete' || e.key === 'Backspace') && selectedId) {
                    e.preventDefault();
                    deleteSelected();
                }
            };

            const handleKeyUp = (e: KeyboardEvent) => {
                if (editingId) {
                    return;
                }

                if (e.code === 'Space' && tempTool) {
                    e.preventDefault();
                    setTool(tempTool);
                    setTempTool(null);
                }
            };

            window.addEventListener('keydown', handleKeyDown);
            window.addEventListener('keyup', handleKeyUp);

            return () => {
                window.removeEventListener('keydown', handleKeyDown);
                window.removeEventListener('keyup', handleKeyUp);
            };
        }
    }, [tool, tempTool, editingId, undo, redo, deleteSelected, selectedId, annotateModalOpen]);

    useEffect(() => {
        if (selectedId) {
            const selectedNode = stageRef.current?.findOne('#' + selectedId);
            if (selectedNode && trRef.current) {
                trRef.current.nodes([selectedNode]);
                trRef.current.getLayer()?.batchDraw();
            }
        }
    }, [selectedId]);

    const toolButtons: ToolButton[] = [
        { icon: <Move size={18} />, name: 'move', tooltip: 'Move/Select' },
        { icon: <Hand size={18} />, name: 'pan', tooltip: 'Pan (Space)' },
        { icon: <Square size={18} />, name: 'rect', tooltip: 'Rectangle' },
        { icon: <CircleIcon size={18} />, name: 'circle', tooltip: 'Circle' },
        { icon: <ArrowRight size={18} />, name: 'arrow', tooltip: 'Arrow' },
        { icon: <Type size={18} />, name: 'text', tooltip: 'Text' },
        { icon: <Pencil size={18} />, name: 'pen', tooltip: 'Pen' },
    ];

    return (
        <ResponsiveDialog open={annotateModalOpen} onOpenChange={updateAnnotateModalOpen}>
            <ResponsiveDialog.Content
                aria-describedby={undefined}
                container={document.getElementById('root-atarim')?.shadowRoot?.getElementById('app-wrapper')}
                className="flex flex-col h-[90vh] w-[90vw] max-w-full overflow-hidden bg-background gap-0 p-0 z-50">
                <div className="flex flex-1 min-h-0 relative ">
                    <div className="flex h-14 items-center absolute top-4 left-1/2 gap-3 p-2 border border-border z-50 bg-background rounded-xl -translate-x-1/2"
                        style={{ boxShadow: '0 0 0 1px color-mix(in oklab, #0000330f, #f0f0f3 25%), 0 12px 60px #00000026, 0 16px 64px #00005506, 0 16px 36px -20px #00062e12' }}>
                        {toolButtons.map((toolBtn) => (
                            <Button
                                key={toolBtn.name}
                                variant={tool === toolBtn.name ? "primary" : "outline"}
                                size="icon"
                                className={cn(tool === toolBtn.name && 'bg-tertiary border-tertiary text-white', 'p-2')}
                                onClick={() => setTool(toolBtn.name)}
                                title={toolBtn.tooltip}
                            >
                                {toolBtn.icon}
                            </Button>
                        ))}
                        <SizeControl
                            size={size}
                            setSize={setSize}
                            updateSelectedShapeSize={updateSelectedShapeSize}
                        />
                        <ColorPicker
                            portal={false}
                            value={color}
                            onChange={(color) => {
                                setColor(color);
                                updateSelectedShapeColor(color);
                            }}
                            children={
                                <div className="w-4 h-4 rounded-full border" style={{ backgroundColor: color }} />
                            }
                            variant="outline"
                            size="sm"
                            rightIcon={<Pipette size={18} />}
                        />
                        <Button
                            variant="outline"
                            size="icon"
                            className="p-2 hover:text-destructive"
                            onClick={deleteSelected}
                            disabled={!selectedId}
                            title="Delete (Del)"
                        >
                            <Trash2 size={18} />
                        </Button>

                    </div>

                    <div ref={containerRef} className="flex-1 p-2">
                        <Stage
                            ref={stageRef}
                            width={stageSize.width}
                            height={stageSize.height}
                            scale={{ x: scale, y: scale }}
                            position={position}
                            draggable={tool === 'pan'}
                            onMouseDown={handleMouseDown}
                            onMouseMove={handleMouseMove}
                            onMouseUp={handleMouseUp}
                            onDragEnd={(e) => {
                                if (tool === 'pan') {
                                    setPosition(e.target.position());
                                }
                            }}
                            className="bg-gray-100 rounded-lg"
                            style={{ cursor: getCursor() }}
                        >
                            <Layer>
                                <Image
                                    image={image}
                                    listening={tool !== 'pan'}
                                />
                                {shapes.map((shape) => {
                                    if (shape.type === 'rect') {
                                        return (
                                            <Rect
                                                key={shape.id}
                                                id={shape.id}
                                                x={shape.x}
                                                y={shape.y}
                                                width={shape.width}
                                                height={shape.height}
                                                stroke={shape.stroke}
                                                onDragEnd={addToHistory}
                                                onTransformEnd={addToHistory}
                                                strokeWidth={shape.strokeWidth || 2}
                                                onClick={(_: Konva.KonvaEventObject<MouseEvent>) => {
                                                    if (tool === 'move') {
                                                        if (editingId === shape.id) {
                                                            setEditingId(null);
                                                        } else {
                                                            setSelectedId(shape.id);
                                                        }
                                                    }
                                                }}
                                                draggable={tool === 'move'}
                                            />
                                        );
                                    } else if (shape.type === 'circle') {
                                        return (
                                            <Circle
                                                key={shape.id}
                                                id={shape.id}
                                                x={shape.x}
                                                y={shape.y}
                                                radius={shape.radius}
                                                stroke={shape.stroke}
                                                strokeWidth={shape.strokeWidth || 2}
                                                onDragEnd={addToHistory}
                                                onTransformEnd={addToHistory}
                                                onClick={(_: Konva.KonvaEventObject<MouseEvent>) => {
                                                    if (tool === 'move') {
                                                        if (editingId === shape.id) {
                                                            setEditingId(null);
                                                        } else {
                                                            setSelectedId(shape.id);
                                                        }
                                                    }
                                                }}
                                                draggable={tool === 'move'}
                                            />
                                        );
                                    } else if (shape.type === 'text') {
                                        return (
                                            <EditableText
                                                key={shape.id}
                                                shape={shape}
                                                shapes={shapes}
                                                setShapes={setShapes}
                                                fontSize={shape.fontSize || 14}
                                                isEditing={editingId === shape.id}
                                                addToHistory={addToHistory}
                                                tool={tool}
                                                onToggleEdit={() => {
                                                    if (editingId === shape.id) {
                                                        setEditingId(null);
                                                        addToHistory();
                                                    } else {
                                                        setEditingId(shape.id);
                                                    }
                                                }}
                                                onChange={(newText) => {
                                                    const shapes_copy = [...shapes];
                                                    const textShape = shapes_copy.find(s => s.id === shape.id);
                                                    if (textShape) {
                                                        textShape.text = newText;
                                                        setShapes(shapes_copy);
                                                    }
                                                }}
                                                onResize={(width, height) => {
                                                    const shapes_copy = [...shapes];
                                                    const textShape = shapes_copy.find(s => s.id === shape.id);
                                                    if (textShape) {
                                                        textShape.width = width;
                                                        textShape.height = height;
                                                        // Reset scales
                                                        textShape.scaleX = 1;
                                                        textShape.scaleY = 1;
                                                        setShapes(shapes_copy);
                                                        addToHistory();
                                                    }
                                                }}
                                                onSelect={() => {
                                                    setSelectedId(shape.id);
                                                }}
                                            />
                                        );
                                    } else if (shape.type === 'arrow' && shape.points) {
                                        return (
                                            <TaperedArrow
                                                key={shape.id}
                                                id={shape.id}
                                                x={shape.x}
                                                y={shape.y}
                                                points={shape.points}
                                                stroke={shape.stroke}
                                                strokeWidth={shape.strokeWidth || 2}
                                                hitStrokeWidth={20}
                                                draggable={tool === 'move'}
                                                onDragEnd={addToHistory}
                                                onTransformEnd={addToHistory}
                                                onClick={(_: Konva.KonvaEventObject<MouseEvent>) => {
                                                    if (tool === 'move') {
                                                        if (editingId === shape.id) {
                                                            setEditingId(null);
                                                        } else {
                                                            setSelectedId(shape.id);
                                                        }
                                                    }
                                                }}
                                                onTransform={(e) => {
                                                    const node = e.target;
                                                    const scaleX = node.scaleX();
                                                    const scaleY = node.scaleY();

                                                    const shapes_copy = [...shapes];
                                                    const shape = shapes_copy.find(s => s.id === node.id());
                                                    if (shape && shape.points) {
                                                        const newPoints = [
                                                            shape.points[0],
                                                            shape.points[1],
                                                            shape.points[2] * scaleX,
                                                            shape.points[3] * scaleY
                                                        ];
                                                        shape.points = newPoints;

                                                        node.scaleX(1);
                                                        node.scaleY(1);

                                                        setShapes(shapes_copy);
                                                    }
                                                }}
                                            />
                                        );
                                    }
                                })}
                                {lines.map((line) => (
                                    <Line
                                        key={line.id}
                                        points={line.points}
                                        stroke={line.stroke}
                                        strokeWidth={line.strokeWidth || 2}
                                        tension={0.5}
                                        lineCap="round"
                                        onDragEnd={addToHistory}
                                        onTransformEnd={addToHistory}
                                        onClick={(_: Konva.KonvaEventObject<MouseEvent>) => {
                                            if (tool === 'move') {
                                                if (editingId === line.id) {
                                                    setEditingId(null);
                                                } else {
                                                    setSelectedId(line.id);
                                                }
                                            }
                                        }}
                                        draggable={tool === 'move'}
                                    />
                                ))}
                                {selectedId && !editingId && (
                                    <Transformer
                                        ref={trRef}
                                        boundBoxFunc={(_, newBox) => {
                                            // Get the selected shape
                                            const shape = shapes.find(s => s.id === selectedId);
                                            if (shape?.type === 'text') {
                                                // Allow both width and height changes with minimum sizes
                                                newBox.width = Math.max(30, newBox.width);
                                                newBox.height = Math.max(30, newBox.height);
                                            } else {
                                                // For other shapes, maintain minimum size
                                                newBox.width = Math.max(30, newBox.width);
                                                newBox.height = Math.max(30, newBox.height);
                                            }
                                            return newBox;
                                        }}
                                        // Enable all resize anchors for text too
                                        enabledAnchors={[
                                            'top-left',
                                            'top-center',
                                            'top-right',
                                            'middle-right',
                                            'middle-left',
                                            'bottom-left',
                                            'bottom-center',
                                            'bottom-right'
                                        ]}
                                        rotateEnabled={shapes.find(s => s.id === selectedId)?.type !== 'text'}
                                        keepRatio={false}
                                        anchorCornerRadius={2}
                                        resizeEnabled={true}
                                    />
                                )}
                            </Layer>
                        </Stage>
                    </div>
                </div>

                <div className="flex justify-between gap-2 p-4 border-t border-border bg-background z-50">
                    <div className="flex gap-2">
                        <div className="flex gap-2 border border-border rounded-lg items-center p-0 h-9">
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => setScale(Math.max(0.1, scale - 0.1))}
                                className="p-2"
                            >
                                <Minus size={18} />
                            </Button>
                            <span className="flex items-center justify-center text-textcolor">{Math.round(scale * 100)}%</span>
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => setScale(Math.min(2, scale + 0.1))}
                                className="p-2"
                            >
                                <Plus size={18} />
                            </Button>
                        </div>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={fitToScreen}
                        >
                            Fit to Screen
                        </Button>
                        <Button
                            variant="outline"
                            size="icon"
                            className="p-2"
                            onClick={undo}
                            disabled={historyStep === 0}
                            title="Undo"
                        >
                            <Undo2 size={18} />
                        </Button>
                        <Button
                            variant="outline"
                            size="icon"
                            className="p-2"
                            onClick={redo}
                            disabled={historyStep === history.length - 1}
                            title="Redo"
                        >
                            <Redo2 size={18} />
                        </Button>
                    </div>
                    <div className="flex gap-2">

                        <Button size="sm" variant="outline" onClick={() => updateAnnotateModalOpen(false)}>Cancel</Button>
                        <Button size="sm" onClick={async () => {
                            if (!stageRef.current) return;

                            setSelectedId(null);
                            await new Promise(resolve => setTimeout(resolve, 0));
                            // Store original dimensions and scale
                            const originalScale = stageRef.current.scale();
                            const originalPosition = stageRef.current.position();

                            // Temporarily adjust stage to fit image exactly
                            stageRef.current.scale({ x: 1, y: 1 });
                            stageRef.current.position({ x: 0, y: 0 });

                            // Set stage size to match image size
                            const imageNode = stageRef.current.findOne('Image');
                            if (imageNode) {
                                stageRef.current.width(imageNode.width());
                                stageRef.current.height(imageNode.height());
                            }



                            // Get the data URL from the stage
                            const dataUrl = stageRef.current.toDataURL();

                            // Restore original stage properties
                            stageRef.current.scale(originalScale);
                            stageRef.current.position(originalPosition);
                            stageRef.current.width(stageSize.width);
                            stageRef.current.height(stageSize.height);

                            // Convert the data URL to a blob
                            const response = await fetch(dataUrl);
                            const blob = await response.blob();

                            // Create a File object from the blob
                            const file = new File([blob], `${siteInfo?.name}_${Date.now()}.png`, { type: 'image/png' });

                            // Add to pending files
                            setFiles([...files, file]);

                            // Close the editor
                            updateAnnotateModalOpen(false);
                        }}>Save</Button>
                    </div>
                </div>
            </ResponsiveDialog.Content>
        </ResponsiveDialog>
    );
};

export default PhotoEditor;

interface EditableTextProps {
    shape: Shape;
    fontSize: number;
    isEditing: boolean;
    onToggleEdit: () => void;
    onChange: (text: string) => void;
    onResize: (width: number, height: number) => void;
    onSelect: () => void;
    addToHistory: () => void;
    tool: string;
    shapes: Shape[];
    setShapes: (shapes: Shape[]) => void;
}

const EditableText = ({
    shape,
    fontSize,
    isEditing,
    onToggleEdit,
    onChange,
    onResize,
    onSelect,
    addToHistory,
    tool,
    shapes,
    setShapes,
}: EditableTextProps) => {
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    useEffect(() => {
        if (isEditing && textareaRef.current) {
            // Auto-adjust height to content
            textareaRef.current.style.height = 'auto';
            textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
        }
    }, [isEditing, shape.text]);

    useEffect(() => {
        if (isEditing) {
            const handleClickOutside = (e: MouseEvent) => {
                if (textareaRef.current && !textareaRef.current.contains(e.target as Node)) {
                    // Update shape dimensions based on textarea size
                    if (textareaRef.current) {
                        onResize(
                            textareaRef.current.scrollWidth,
                            textareaRef.current.scrollHeight
                        );
                    }
                    onToggleEdit();
                    addToHistory();
                }
            };

            document.addEventListener('mousedown', handleClickOutside);
            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
            };
        }
    }, [isEditing, onToggleEdit, addToHistory, onResize]);

    if (isEditing) {
        return (
            <Html
                groupProps={{
                    x: shape.x,
                    y: shape.y,
                    scaleX: shape.scaleX || 1,
                    scaleY: shape.scaleY || 1
                }}
                divProps={{ style: { opacity: 1 } }}
            >
                <textarea
                    ref={textareaRef}
                    value={shape.text || ''}
                    onChange={(e) => {
                        onChange(e.currentTarget.value);
                        // Auto-adjust height
                        e.currentTarget.style.height = 'auto';
                        e.currentTarget.style.height = `${e.currentTarget.scrollHeight}px`;
                    }}
                    onKeyDown={(e) => {
                        if ((e.key === 'Enter' && !e.shiftKey) || e.key === 'Escape') {
                            if (textareaRef.current) {
                                // Update shape dimensions based on textarea size
                                onResize(
                                    textareaRef.current.scrollWidth,
                                    textareaRef.current.scrollHeight
                                );
                            }
                            onToggleEdit();
                            addToHistory();
                        }
                    }}
                    style={{
                        width: `${shape.width || 150}px`,
                        minHeight: `${fontSize + 4}px`,
                        border: 'none',
                        padding: '0px',
                        margin: '0px',
                        background: 'none',
                        outline: 'none',
                        resize: 'none',
                        fontSize: `${fontSize}px`,
                        fontFamily: 'sans-serif',
                        color: shape.fill,
                        overflow: 'hidden',
                        lineHeight: '1.2',
                        display: 'block',
                    }}
                    autoFocus
                />
            </Html>
        );
    }

    return (
        <KonvaText
            id={shape.id}
            x={shape.x}
            y={shape.y}
            text={shape.text || ''}
            fontSize={fontSize}
            fill={shape.fill}
            width={shape.width}
            height={shape.height}
            draggable={tool === 'move'}
            onDragEnd={addToHistory}
            onClick={(_: Konva.KonvaEventObject<MouseEvent>) => {
                if (tool === 'move') {
                    onSelect();
                }
            }}
            onDragMove={(e) => {
                const shapes_copy = [...shapes];
                const textShape = shapes_copy.find(s => s.id === shape.id);
                if (textShape) {
                    textShape.x = e.target.x();
                    textShape.y = e.target.y();
                    setShapes(shapes_copy);
                }
            }}
            onDblClick={(_: Konva.KonvaEventObject<MouseEvent>) => {
                if (tool === 'move') {
                    onToggleEdit();
                }
            }}
            onTransform={(e) => {
                const node = e.target;
                const scaleX = node.scaleX();
                const scaleY = node.scaleY();

                // Get the current position
                const x = node.x();
                const y = node.y();

                // Reset scale and adjust width and height
                node.scaleX(1);
                node.scaleY(1);

                // Calculate new dimensions based on scale, with minimum sizes
                const newWidth = Math.max(node.width() * scaleX, fontSize * 2);
                const newHeight = Math.max(node.height() * scaleY, fontSize * 1.2);

                // Update both width and height
                onResize(newWidth, newHeight);

                // Restore position
                node.position({ x, y });

                // Force update
                node.getLayer()?.batchDraw();
            }}
            onTransformEnd={() => {
                addToHistory();
            }}
            wrap="word"
            align="left"
            padding={5} // Add some padding to make text more readable
        />
    );
};


const SizeControl = ({
    size,
    setSize,
    updateSelectedShapeSize
}: {
    size: number;
    setSize: (size: number) => void;
    updateSelectedShapeSize: (newSize: number) => void;
}) => {
    return (
        <div className="flex gap-2 items-center">
            <Button
                variant="outline"
                size="icon"
                onClick={() => {
                    const newSize = Math.max(1, size - 1);
                    setSize(newSize);
                    updateSelectedShapeSize(newSize);
                }}
                className="p-2"
            >
                <Minus size={18} />
            </Button>
            <span className='text-textcolor'>{size}px</span>
            <Button
                variant="outline"
                size="icon"
                onClick={() => {
                    const newSize = Math.min(20, size + 1);
                    setSize(newSize);
                    updateSelectedShapeSize(newSize);
                }}
                className="p-2"
            >
                <Plus size={18} />
            </Button>

        </div >
    );
};

interface TaperedArrowProps {
    points: number[];
    stroke?: string;
    strokeWidth: number;
    id?: string;
    x?: number;
    y?: number;
    hitStrokeWidth?: number;
    draggable?: boolean;
    onDragEnd?: () => void;
    onTransformEnd?: () => void;
    onClick?: (e: Konva.KonvaEventObject<MouseEvent>) => void;
    onTransform?: (e: Konva.KonvaEventObject<Event>) => void;
}

const TaperedArrow = ({
    points,
    stroke,
    strokeWidth,
    ...props
}: TaperedArrowProps) => {
    // Calculate the angle and length
    const dx = points[2] - points[0];
    const dy = points[3] - points[1];
    const angle = Math.atan2(dy, dx);
    const length = Math.sqrt(dx * dx + dy * dy);

    // Create points for a tapered shape
    const startWidth = strokeWidth;
    const endWidth = strokeWidth * 10; // Three times wider at the end
    const arrowHeadLength = Math.min(length * 0.4, strokeWidth * 12); // Cap the arrow head length
    const arrowHeadWidth = endWidth * 2;

    // Calculate the points for the tapered arrow body
    const shape = [
        // Starting point (thin end)
        points[0] - (startWidth / 2) * Math.sin(angle),
        points[1] + (startWidth / 2) * Math.cos(angle),
        // Ending point wide end (before arrow head)
        points[2] - (endWidth / 2) * Math.sin(angle) - arrowHeadLength * Math.cos(angle),
        points[3] + (endWidth / 2) * Math.cos(angle) - arrowHeadLength * Math.sin(angle),
        // Arrow head base (wide)
        points[2] - (arrowHeadWidth / 2) * Math.sin(angle) - arrowHeadLength * Math.cos(angle),
        points[3] + (arrowHeadWidth / 2) * Math.cos(angle) - arrowHeadLength * Math.sin(angle),
        // Arrow head tip
        points[2],
        points[3],
        // Other side of arrow head
        points[2] + (arrowHeadWidth / 2) * Math.sin(angle) - arrowHeadLength * Math.cos(angle),
        points[3] - (arrowHeadWidth / 2) * Math.cos(angle) - arrowHeadLength * Math.sin(angle),
        // Back to body (wide end)
        points[2] + (endWidth / 2) * Math.sin(angle) - arrowHeadLength * Math.cos(angle),
        points[3] - (endWidth / 2) * Math.cos(angle) - arrowHeadLength * Math.sin(angle),
        // Back to start (thin end)
        points[0] + (startWidth / 2) * Math.sin(angle),
        points[1] - (startWidth / 2) * Math.cos(angle),
    ];

    return (
        <Line
            points={shape}
            closed={true}
            fill={stroke}
            stroke={stroke}
            strokeWidth={1}
            {...props}
        />
    );
};
